import { useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Typography,
  MenuItem,
  Select,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { UpdateRetailerNetworkLocationsPolicy } from '@open-commerce/oc-authorization-policies';

import { CircularProgress, Divider } from '@occc-app/design-system';
import {
  Retailer_Network_Performance_Period,
  RetailerNetworkPerformanceFilter,
} from '@occc/graphql';
import { useCan } from '@occc-app/App/PolicyBasedAccessControl';

import { MonthlyPerformanceGoalFormValues } from './components/specific-goals-dialog/specific-goals-dialog.types';
import { RetailerNetworkPerformance } from './retailer-network-performance.component';
import {
  mapPeriodValueToPeriodType,
  performancePeriodOptions,
} from './retailer-network-performance.constants';
import { SetSpecificGoalBanner } from './components/set-specific-goal-banner.component';
import { RetailerNetworkPerformanceScope } from './retailer-network-performance.types';
import { SpecificGoalDialog } from './components/specific-goals-dialog/specific-goals-dialog.component';

import { useGetRetailerNetworkPerformanceQuery } from '../../../data-access/queries';
import { RetailerNetworkErrorMessage } from '../retailer-network-error-message.component';
import { RetailerNetworkNoData } from '../retailer-network-no-data.component';

type RetailerNetworkPerformanceContainerProps = {
  title?: string;
  filter: Omit<RetailerNetworkPerformanceFilter, 'periodEquals'>;
  setSpecificGoal?: (values: MonthlyPerformanceGoalFormValues) => Promise<void>;
  isSpecificGoalSet?: boolean;
  scope: RetailerNetworkPerformanceScope;
  retailerNetworkGoals: {
    membersPerStore: number;
    transactionsPerStore: number;
  };
};

export const RetailerNetworkPerformanceContainer = ({
  filter,
  title,
  setSpecificGoal,
  isSpecificGoalSet,
  scope,
  retailerNetworkGoals,
}: RetailerNetworkPerformanceContainerProps) => {
  const { networkId } = useParams<{ networkId: string }>();
  const [period, setPeriod] = useState<Retailer_Network_Performance_Period>(
    Retailer_Network_Performance_Period.ThisMonth,
  );
  const [isSpecificGoalDialogOpen, setIsSpecificGoalDialogOpen] = useState<boolean>(false);
  const isUserPermittedTo = useCan();
  const isUserPermittedToEditSpecificGoals =
    !!networkId &&
    isUserPermittedTo(UpdateRetailerNetworkLocationsPolicy('uuid'), { uuid: networkId });

  const { data, loading, error } = useGetRetailerNetworkPerformanceQuery({
    variables: {
      filter: {
        ...filter,
        periodEquals: period,
      },
    },
  });
  const retailerNetworkPerformance = data?.retailerNetworkPerformance;

  const onPeriodChange = (event: SelectChangeEvent<Retailer_Network_Performance_Period>) => {
    const { value } = event.target;
    setPeriod(mapPeriodValueToPeriodType[value]);
  };

  const onSiteSpecificGoalDialogSubmit = async (values: MonthlyPerformanceGoalFormValues) => {
    if (setSpecificGoal) {
      await setSpecificGoal(values);
    }

    setIsSpecificGoalDialogOpen(false);
  };

  const isSetSpecificGoalBannerVisible =
    // For now just scope 'location' is supported
    !isSpecificGoalSet && scope === 'location' && isUserPermittedToEditSpecificGoals && !loading;

  const renderData = () => {
    if (loading && !retailerNetworkPerformance) {
      return (
        <Box p={3} display='flex' justifyContent='center' alignItems='center'>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <RetailerNetworkErrorMessage />;
    }

    if (!loading && !retailerNetworkPerformance) {
      return <RetailerNetworkNoData />;
    }

    return (
      <RetailerNetworkPerformance
        retailerNetworkPerformance={retailerNetworkPerformance}
        period={period}
        isSpecificGoalSet={isSpecificGoalSet}
        openSpecificGoalDialog={() => setIsSpecificGoalDialogOpen(true)}
        scope={scope}
        isUserPermittedToEditSpecificGoals={isUserPermittedToEditSpecificGoals}
      />
    );
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        sx={{ p: 3 }}
        title={<Typography variant='h6'>{title || 'Network Performance'}</Typography>}
        action={
          <Select
            sx={{ width: '140px' }}
            value={period}
            onChange={onPeriodChange}
            size='small'
            disabled={loading}
          >
            {performancePeriodOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        }
      />
      <Divider />
      {loading && data && <LinearProgress />}

      {isSetSpecificGoalBannerVisible && (
        <Box pt={3} px={3}>
          <SetSpecificGoalBanner onActionButtonClick={() => setIsSpecificGoalDialogOpen(true)} />
        </Box>
      )}

      {renderData()}

      <SpecificGoalDialog
        open={isSpecificGoalDialogOpen}
        onClose={() => setIsSpecificGoalDialogOpen(false)}
        onSubmit={onSiteSpecificGoalDialogSubmit}
        retailerNetworkGoals={retailerNetworkGoals}
        isSpecificGoalSet={isSpecificGoalSet}
        specificGoals={{
          membersPerStore: retailerNetworkPerformance?.newMembers.goalProgress.goal || 0,
          transactionsPerStore:
            retailerNetworkPerformance?.loyaltyTransactions.goalProgress.goal || 0,
        }}
      />
    </Card>
  );
};
